import type { FocusEvent, KeyboardEvent } from "react";

const SECTIONS_SELECTOR = ".MuiPickersSectionList-root";

/**
 * MUI X date fields (accessible section-based DOM) render one focusable span per date section, so
 * `onBlur` fires on every move between sections and also when focus leaves the open or clear button.
 * True only when focus leaves the sections for somewhere outside them: the moment the old <input>
 * would have blurred.
 */
export const isLeavingDateSections = (event: FocusEvent<HTMLElement>): boolean => {
  const sections = event.currentTarget.querySelector(SECTIONS_SELECTOR) ?? event.currentTarget;
  const fromSection = event.target instanceof Node && sections.contains(event.target);
  const toSection = event.relatedTarget instanceof Node && sections.contains(event.relatedTarget);
  return fromSection && !toSection;
};

// Input types that block implicit submission (HTML spec, "implicit submission").
const BLOCKING_INPUT_TYPES = new Set([
  "text",
  "search",
  "url",
  "tel",
  "email",
  "password",
  "date",
  "month",
  "week",
  "time",
  "datetime-local",
  "number",
]);

const isSubmitButton = (element: Element): element is HTMLButtonElement | HTMLInputElement =>
  (element instanceof HTMLButtonElement && element.type === "submit") ||
  (element instanceof HTMLInputElement && (element.type === "submit" || element.type === "image"));

/**
 * Enter in a native <input> submits its form ("implicit submission"), but the section spans of the
 * accessible date field do not. This restores the browser's rule for date fields: click the form's
 * default (first) submit button unless it is disabled; with no submit button, submit only a form
 * whose single text-like field is this one. Nothing happens in a disabled field.
 *
 * Pickers 9 submit the closest form on Enter whenever it has any submit button, even a disabled one
 * (a second upload while the first is in flight). Setting `defaultMuiPrevented` stops that, so this
 * handler is the only Enter behaviour. Pickers 9 also take a disabled field out of the tab order;
 * the disabled check stays for programmatic focus.
 */
export const submitFormOnEnter = (event: KeyboardEvent<HTMLElement>): void => {
  if (event.key !== "Enter") return;
  (event as KeyboardEvent<HTMLElement> & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;

  const field = event.currentTarget.closest(".MuiFormControl-root") ?? event.currentTarget;
  if (field.querySelector("input")?.disabled) return;

  const form = event.currentTarget.closest("form");
  if (!form) return;

  const elements = Array.from(form.elements);
  const defaultButton = elements.find(isSubmitButton);
  if (defaultButton) {
    // A disabled button ignores click(), exactly as it ignores a native Enter.
    defaultButton.click();
    return;
  }
  const blockingFields = elements.filter(
    (element) => element instanceof HTMLInputElement && BLOCKING_INPUT_TYPES.has(element.type),
  );
  if (blockingFields.length === 1) {
    form.requestSubmit();
  }
};
